<template>
  <v-dialog v-model="isDialogOpen" width="auto" style="max-width: 800px">
    <v-card>
      <v-card-title>{{ $t('pwa.dialogTitle') }}</v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" md="6" class="d-flex align-center justify-center">
              <logo />
            </v-col>
            <v-col cols="12" md="6">
              <div>
                {{ $t('pwa.dialogBody', { subdomain: getSubdomain() }) }}
              </div>
              <v-list>
                <v-list-item v-for="(message, index) in $tm('pwa.bulletPoints')" :key="index">
                  {{ message }}
                </v-list-item>
              </v-list>
            </v-col>
            <v-col v-if="platformIosBrowser" class="ga-3">
              <p class="text-h6 mb-3">
                {{ $t('pwa.ios.title') }}
              </p>
              <p class="mb-3">
                {{ $t('pwa.ios.text') }}
              </p>

              <v-list>
                <v-list-item>
                  1. {{ $t('pwa.ios.steps.share') }}
                  <v-icon icon="mdi-export-variant" class="ml-1" />
                </v-list-item>
                <v-list-item>
                  2. {{ $t('pwa.ios.steps.add') }}
                </v-list-item>
                <v-list-item>
                  3. {{ $t('pwa.ios.steps.confirm') }}
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions class="ga-5">
        <v-spacer />
        <template v-if="platformIosBrowser">
          <v-btn-neutral class="text-capitalize" rounded @click="dismiss">
            {{ $t('close') }}
          </v-btn-neutral>
        </template>
        <template v-else>
          <v-btn-neutral rounded @click="decline">
            {{ $t('pwa.decline') }}
          </v-btn-neutral>
          <v-btn-primary
            @click="accept"
          >
            <v-icon icon="mdi-download" />
            {{ $t('pwa.accept') }}
          </v-btn-primary>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { getSubdomain } from '@/helpers/util';
import Logo from '@/components/PortalLogo.vue';
import { usePwaStore } from '@/store/pwa';
import { storeToRefs } from 'pinia';
import { getLocalstorageItem, setLocalstorageItem } from '@/helpers/localStorage';

const pwaStore = usePwaStore();
const { showInstallPrompt } = pwaStore;
const { deferredPrompt } = storeToRefs(pwaStore);

const isDialogOpen = ref(false);

onMounted(async () => {
  if (navigator.webdriver) {
    return;
  }

  window.happitDataLayer.push({
    event: 'pwa_status',
    pwa_mode: getDisplayMode(),
    platform: navigator.userAgent,
  });

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt.value = e;

    window.happitDataLayer.push({
      event: 'pwa_install_prompt',
    });

    if (isPWADeclined()) {
      window.happitDataLayer.push({
        event: 'pwa_install_prompt_dialog',
        outcome: 'dismissed_historic',
      });
      return;
    }

    open();
  });

  setTimeout(iosFallback, 1000);
});

function getDisplayMode() {
  if (document.referrer.startsWith('android-app://')) {
    return 'twa';
  }
  if (window.matchMedia('(display-mode: browser)').matches) {
    return 'browser';
  }
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return 'standalone';
  }
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui';
  }
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen';
  }
  if (window.matchMedia('(display-mode: window-controls-overlay)').matches) {
    return 'window-controls-overlay';
  }

  return 'unknown';
}

function getPlatformIos() {
  return /iPad|iPhone|iPod|Mac/.test(navigator.userAgent);
}

const iosFallback = () => {
  if (platformIosBrowser && !isPWADeclined()) {
    open();
  }
};

function isPWADeclined() {
  return getLocalstorageItem('PWADeclined');
}

function declinePWA() {
  setLocalstorageItem('PWADeclined', true, 30);
}

function open() {
  isDialogOpen.value = true;
}

function close() {
  isDialogOpen.value = false;
}

const dismiss = () => {
  declinePWA();
  close();
};

const decline = () => {
  close();

  window.happitDataLayer.push({
    event: 'pwa_install_prompt_dialog',
    outcome: 'dismissed',
  });

  declinePWA();
};

const accept = () => {
  close();

  window.happitDataLayer.push({
    event: 'pwa_install_prompt_dialog',
    outcome: 'accepted',
  });

  showInstallPrompt();
};

const platformIosBrowser = getPlatformIos() && (getDisplayMode() === 'browser');
</script>
